import type {
    DriverInfo,
    LoadedInfo,
    LoadInIframeResult,
    OfficeWebHostScope,
} from '@ms/office-web-host';
import type { PerformanceDatapoint } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { timestamp } from 'owa-datetime';
import * as trace from 'owa-trace';
import { getDriverDetailsForFluidItem } from './getDriverDetailsForFluidItem';
import { renderFluidComponent } from '../../components/FluidComponent';
import { getFluidPropsForRender } from '../fluidProps/getFluidPropsForRender';
import {
    getNotificationServicePromise,
    getShareServicePromise,
} from '../fluidProps/hostContext/scopeHelpers';
import type { FluidOwaSource, FluidOwaScenario } from 'owa-fluid-validations';
import { reportPerfCheckmark, OWAFluidCheckmarksEnum } from 'owa-fluid-perf-datapoints';
import type { CommandProps } from '@fluidx/office-fluid-types';
import { getScenarioNameFromScenarioAndSource } from '../getScenarioNameFromScenarioAndSource';

// Generic method to mount fluid components where the caller handles the insertion and
// the clean up. Used for fluid in any surface besides Editor, such as readingPane, calendar, and spaces
export async function mountFluidComponentExternal(
    targetDiv: HTMLDivElement,
    driverInfo: DriverInfo,
    scenario: FluidOwaScenario,
    scenarioStartTime: number,
    perfDatapoint: PerformanceDatapoint,
    owaSource: FluidOwaSource,
    headerNode: Node,
    url: string,
    controlNode?: React.ReactElement<{}>,
    customMenuItems?: CommandProps[],
    addRecipientFunc?: (address: string) => void
): Promise<LoadedInfo> {
    const scenarioName: string = getScenarioNameFromScenarioAndSource(scenario, owaSource);
    perfDatapoint.addCustomData({
        differenceFromStartTime: timestamp() - scenarioStartTime,
        scenario: scenarioName,
    });
    // Get OWA's session Id from config
    const sessionId = getSessionId();
    const mountComponentProps = getFluidPropsForRender(
        sessionId,
        scenarioName,
        scenarioStartTime,
        owaSource,
        undefined /* codeDetails */,
        url,
        undefined /* deleteNode */,
        customMenuItems
    );
    // We need to resolve the driverDetails for an already existing fluid component
    // after which we can instantiate the scope object.
    const driverDetails = getDriverDetailsForFluidItem(driverInfo.documentUrl);
    perfDatapoint.addCheckmark('fetchedDriverDetails');
    reportPerfCheckmark(OWAFluidCheckmarksEnum.driverDetailsFetched, owaSource);
    if (!driverDetails) {
        trace.errorThatWillCauseAlert('Could not create driver details for existing fluid item');
        throw new Error('failed to create driver details');
    }
    const scope: OfficeWebHostScope = {
        NotificationServicePromise: getNotificationServicePromise(
            mountComponentProps.tokenProvider,
            driverDetails.itemId,
            driverDetails.driveId,
            driverDetails.siteUrl,
            mountComponentProps.hostConfig.logger,
            sessionId,
            addRecipientFunc
        ),
        ShareServicePromise: getShareServicePromise(
            mountComponentProps.tokenProvider,
            mountComponentProps.hostConfig.logger,
            driverDetails.itemId,
            driverDetails.driveId,
            driverDetails.siteUrl,
            addRecipientFunc
        ),
        StylingProvider: mountComponentProps.stylingProvider,
        PresenceColorProvider: mountComponentProps.presenceColorProvider,
        HostContext: mountComponentProps.hostContext,
        TokenProvider: mountComponentProps.tokenProvider,
        CommandingSurfaceViewProvider: mountComponentProps.commandingSurfaceParts.viewProvider,
    };

    const result: LoadInIframeResult = await new Promise(resolve => {
        renderFluidComponent(
            mountComponentProps,
            scope,
            targetDiv,
            driverInfo,
            headerNode,
            resolve,
            owaSource,
            false, // isCreateNew
            controlNode
        );
    });
    const loadedInfo = await result.loadedInfo;
    perfDatapoint.addCheckmark('fetchedLoadedInfo');
    reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerBootStarted, owaSource);
    await Promise.all([loadedInfo.container, loadedInfo.component]);
    perfDatapoint.addCheckmark('componentAndContainerResolved');
    reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerResolved, owaSource);
    reportPerfCheckmark(OWAFluidCheckmarksEnum.fluidFileLoaded, owaSource);
    perfDatapoint.end();

    return loadedInfo;
}
